/** Strict AllResults v1 with preserved v1 and accepted v2 sensor sections. */
function object(value: unknown, fields: readonly string[]): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.keys(value).length !== fields.length || fields.some(key => !(key in value))) throw new TypeError("Invalid metrics result fields");
  return value as Record<string, unknown>;
}
function numeric(value: unknown, nonnegative = true): number {
  if (typeof value !== "number" || !Number.isFinite(value) || Math.abs(value) > 1e12 || (nonnegative && value < 0)) throw new TypeError("Invalid metrics result number");
  return value;
}
const statisticKeys = ["tib","spt","waso","tst","n1","n2","n3","rem","nrem","sol","lat_n1","lat_n2","lat_n3","lat_rem","pct_n1","pct_n2","pct_n3","pct_rem","pct_nrem","se","sme","sfi"];
const nullable = new Set(["sol","lat_n1","lat_n2","lat_n3","lat_rem"]);
function matrix(value: unknown, bins: number, count = false): number[][] {
  if (!Array.isArray(value) || value.length < 1 || value.length > 672) throw new TypeError("Invalid metrics result snapshots");
  let previous = Array<number>(bins).fill(0);
  for (const row of value) {
    if (!Array.isArray(row) || row.length !== bins) throw new TypeError("Invalid metrics result dimensions");
    row.forEach((item, i) => { if (numeric(item) < previous[i] || (count && !Number.isSafeInteger(item))) throw new TypeError("Invalid cumulative metrics result"); });
    previous = row;
  }
  return value as number[][];
}
function histogram(value: unknown, bins: number): number[][] {
  const v=object(value,["bin_width_deg","window_s","seconds"]);
  if(v.bin_width_deg!==1||v.window_s!==900) throw new TypeError("Unsupported metrics histogram metadata");
  const rows=matrix(v.seconds,bins);
  rows.forEach((row,i)=>{if(row.reduce((sum,v,j)=>sum+v-(i?rows[i-1][j]:0),0)>900+1e-6) throw new TypeError("Invalid histogram duration");});
  return rows;
}
export function parseMetricsResult(value: unknown, sensorVersion?: 1 | 2): Record<string, unknown> {
  if(new TextEncoder().encode(JSON.stringify(value)).length>2*1024**2) throw new TypeError("Metrics result size limit");
  const v=object(value,["schema_version","hypnogram","events","headband","chestbelt","spo2"]);
  if(v.schema_version!==1) throw new TypeError("Unsupported metrics result version");
  if(sensorVersion!==undefined&&v.hypnogram===null)throw new TypeError("Computed hypnogram missing");
  if(v.hypnogram!==null){
    const h=object(v.hypnogram,["schema_version","sleep_statistics","sleep_scores"]);
    if(h.schema_version!==1) throw new TypeError("Unsupported hypnogram version");
    object(h.sleep_scores,[]);const stats=object(h.sleep_statistics,statisticKeys);
    for(const key of statisticKeys) if(!(stats[key]===null&&nullable.has(key))) numeric(stats[key],false);
  }
  if(v.events!==null&&object(v.events,["schema_version"]).schema_version!==1) throw new TypeError("Unsupported events version");
  const versions=new Set<number>(), snapshots=new Set<number>();
  const orientation: Record<string,number[][]|null>={};
  for(const name of ["headband","chestbelt"]){
    const raw=v[name];if(raw===null){orientation[name]=null;continue;}
    if(!raw||typeof raw!=="object"||Array.isArray(raw)) throw new TypeError("Invalid orientation section");
    const version=(raw as Record<string,unknown>).schema_version;
    if(version===1){object(raw,["schema_version"]);versions.add(1);orientation[name]=null;continue;}
    const section=object(raw,["schema_version","position_histogram","tilt_histogram"]);
    if(version!==2) throw new TypeError("Unsupported orientation version");
    versions.add(2);
    const position=histogram(section.position_histogram,360), tilt=histogram(section.tilt_histogram,180);
    if(position.length!==tilt.length) throw new TypeError("Position/tilt snapshot mismatch");
    snapshots.add(position.length);orientation[name]=position;
    position.forEach((row,i)=>{
      const p=row.reduce((a,b,j)=>a+b-(i?position[i-1][j]:0),0), t=tilt[i].reduce((a,b,j)=>a+b-(i?tilt[i-1][j]:0),0);
      if(p>t+1e-6) throw new TypeError("Position exceeds tilt support");
    });
  }
  if(v.spo2!==null){
    const raw=v.spo2;
    if(!raw||typeof raw!=="object"||Array.isArray(raw)) throw new TypeError("Invalid SpO2 section");
    const version=(raw as Record<string,unknown>).schema_version;
    if(version===1){object(raw,["schema_version"]);versions.add(1);}
    else{
      const section=object(raw,["schema_version","head_position","body_position"]);
      if(version!==2) throw new TypeError("Unsupported SpO2 version");versions.add(2);
      for(const [field,sensor] of [["head_position","headband"],["body_position","chestbelt"]]){
        if(section[field]===null){if(v[sensor]!==null)throw new TypeError("Missing SpO2 sensor support");continue;}
        if(v[sensor]===null)throw new TypeError("SpO2 sensor presence mismatch");
        const h=object(section[field],["bin_width_deg","window_s","sums","counts"]);
        if(h.bin_width_deg!==1||h.window_s!==900)throw new TypeError("Unsupported SpO2 metadata");
        const sums=matrix(h.sums,360),counts=matrix(h.counts,360,true);
        if(sums.length!==counts.length)throw new TypeError("SpO2 snapshot mismatch");snapshots.add(sums.length);
        sums.forEach((row,i)=>row.forEach((sum,j)=>{
          const delta=sum-(i?sums[i-1][j]:0),count=counts[i][j]-(i?counts[i-1][j]:0);
          if((count===0&&delta!==0)||delta>100*count)throw new TypeError("SpO2 sum/count support");
        }));
      }
    }
  }
  if(versions.size>1||snapshots.size>1||(sensorVersion!==undefined&&[...versions].some(version=>version!==sensorVersion)))throw new TypeError("Incompatible metrics section versions");
  return v;
}
